import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { currency } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, LayoutDashboard, Package, Users, Ticket, Star, Image as ImgIcon, Mail, Sparkles, Upload, X } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import CustomOrderDetail, { StatusStrip } from "@/components/CustomOrderDetail";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const emptyProduct = { name: "", category: "resin-art", description: "", short_description: "", price: 0, compare_at_price: null, stock: 10, images: [], materials: [], dimensions: "", is_best_seller: false, is_new_arrival: false, is_featured: false, is_active: true };

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [banners, setBanners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [openCustom, setOpenCustom] = useState(null);
  const [newProduct, setNewProduct] = useState(emptyProduct);
  const [editing, setEditing] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) nav("/auth"); }, [user, loading, nav]);

  const load = async () => {
    const [a, p, o, c, cp, r, bn, tm, g, ct, sb, cust] = await Promise.all([
      api.get("/admin/analytics"), api.get("/products?limit=200"),
      api.get("/admin/orders"), api.get("/admin/customers"),
      api.get("/admin/coupons"), api.get("/admin/reviews"),
      api.get("/banners"), api.get("/testimonials"), api.get("/gallery"),
      api.get("/admin/contact"), api.get("/admin/newsletter"),
      api.get("/custom-orders"),
    ]);
    setAnalytics(a.data); setProducts(p.data); setOrders(o.data); setCustomers(c.data);
    setCoupons(cp.data); setReviews(r.data); setBanners(bn.data); setTestimonials(tm.data);
    setGalleryItems(g.data); setContacts(ct.data); setSubs(sb.data); setCustomOrders(cust.data);
  };
  useEffect(() => { if (user?.role === "admin") load().catch(() => {}); }, [user]);

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newProduct, images: newProduct.images.filter(Boolean), materials: (newProduct.materials || []).filter(Boolean), price: Number(newProduct.price), stock: Number(newProduct.stock), compare_at_price: newProduct.compare_at_price ? Number(newProduct.compare_at_price) : null };
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setNewProduct(emptyProduct); setEditing(null); await load();
    } catch { toast.error("Please try again"); }
  };

  const deleteProduct = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); await load(); };
  const startEdit = (p) => { setEditing(p.id); setNewProduct({ ...emptyProduct, ...p, images: p.images || [], materials: p.materials || [] }); };

  const uploadProductImages = async (files) => {
    if (!files?.length) return;
    setUploadingImages(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is too large (max 8MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND}${data.url}`);
      }
      if (uploaded.length) setNewProduct((np) => ({ ...np, images: [...np.images, ...uploaded] }));
    } catch { toast.error("Upload failed"); } finally { setUploadingImages(false); }
  };

  const removeProductImage = (url) => setNewProduct((np) => ({ ...np, images: np.images.filter((u) => u !== url) }));

  const updateOrderStatus = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); await load(); toast.success("Order updated"); };

  const [newCoupon, setNewCoupon] = useState({ code: "", discount_type: "percent", amount: 10, min_order: 0, is_active: true });
  const createCoupon = async (e) => { e.preventDefault(); await api.post("/admin/coupons", newCoupon); setNewCoupon({ code: "", discount_type: "percent", amount: 10, min_order: 0, is_active: true }); await load(); toast.success("Coupon saved"); };

  const [newBanner, setNewBanner] = useState({ title: "", subtitle: "", image: "", cta_text: "Shop Now", cta_link: "/shop", is_active: true, position: 0 });
  const createBanner = async (e) => { e.preventDefault(); await api.post("/admin/banners", newBanner); setNewBanner({ title: "", subtitle: "", image: "", cta_text: "Shop Now", cta_link: "/shop", is_active: true, position: 0 }); await load(); toast.success("Banner added"); };

  const [newTest, setNewTest] = useState({ name: "", role: "", quote: "", rating: 5, avatar: "" });
  const createTestimonial = async (e) => { e.preventDefault(); await api.post("/admin/testimonials", newTest); setNewTest({ name: "", role: "", quote: "", rating: 5, avatar: "" }); await load(); toast.success("Testimonial saved"); };

  const [newGallery, setNewGallery] = useState({ type: "image", url: "", caption: "", category: "resin-art" });
  const createGallery = async (e) => { e.preventDefault(); await api.post("/admin/gallery", newGallery); setNewGallery({ type: "image", url: "", caption: "", category: "resin-art" }); await load(); toast.success("Gallery item added"); };

  const moderateReview = async (id, approved) => { await api.put(`/admin/reviews/${id}?approved=${approved}`); await load(); };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="border-b divider-gold pb-6 mb-10">
        <div className="overline text-emerald mb-3">Studio · Admin</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Command centre</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <TabsList className="flex lg:flex-col h-auto items-stretch justify-start gap-1 bg-transparent p-0 overflow-x-auto lg:overflow-visible">
          {[
            { v: "overview", i: LayoutDashboard, l: "Overview" },
            { v: "products", i: Package, l: "Products" },
            { v: "orders", i: Package, l: "Orders" },
            { v: "custom", i: Sparkles, l: "Custom orders" },
            { v: "customers", i: Users, l: "Customers" },
            { v: "coupons", i: Ticket, l: "Coupons" },
            { v: "reviews", i: Star, l: "Reviews" },
            { v: "content", i: ImgIcon, l: "Content" },
            { v: "messages", i: Mail, l: "Messages" },
          ].map(({ v, i: Icon, l }) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap">
              <Icon className="w-4 h-4 mr-2" /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="min-w-0">
          <TabsContent value="overview">
            {analytics && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  {[
                    { k: "Revenue", v: currency(analytics.revenue) },
                    { k: "Orders", v: analytics.total_orders },
                    { k: "Pending", v: analytics.pending_orders },
                    { k: "Customers", v: analytics.customers },
                  ].map((s) => (
                    <div key={s.k} className="p-6 bg-ivory/70 border divider-gold">
                      <div className="overline text-charcoal/60">{s.k}</div>
                      <div className="font-serif text-3xl mt-2">{s.v}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                  <div className="lg:col-span-2 p-6 bg-ivory/70 border divider-gold">
                    <div className="overline text-emerald mb-4">Revenue (last 14 days)</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={analytics.sales_series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#D6B36A55" />
                        <XAxis dataKey="date" stroke="#22222299" fontSize={11} />
                        <YAxis stroke="#22222299" fontSize={11} />
                        <Tooltip />
                        <Line type="monotone" dataKey="revenue" stroke="#1F6B5C" strokeWidth={2} dot={{ fill: "#D6B36A" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="p-6 bg-ivory/70 border divider-gold">
                    <div className="overline text-emerald mb-4">Top categories</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={analytics.top_categories} dataKey="revenue" nameKey="category" innerRadius={40} outerRadius={80}>
                          {analytics.top_categories.map((_, i) => <Cell key={i} fill={["#1F6B5C", "#D6B36A", "#222222", "#8FA995"][i % 4]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="p-6 bg-ivory/70 border divider-gold">
                    <div className="overline text-emerald mb-4">Recent orders</div>
                    {analytics.recent_orders.map((o) => (
                      <div key={o.id} className="flex justify-between py-2 border-b divider-gold text-sm">
                        <span>#{o.order_number}</span><span className="text-charcoal/60">{o.status}</span><span>{currency(o.total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 bg-ivory/70 border divider-gold">
                    <div className="overline text-emerald mb-4">Low stock alerts</div>
                    {analytics.low_stock.length === 0 ? <div className="text-charcoal/50 italic">All stocked well.</div> :
                      analytics.low_stock.map((p) => (
                        <div key={p.id} className="flex justify-between py-2 border-b divider-gold text-sm">
                          <span>{p.name}</span><span className="text-emerald">{p.stock} left</span>
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="products">
            <form onSubmit={saveProduct} className="p-6 bg-ivory/60 border divider-gold mb-10">
              <div className="overline text-emerald mb-4">{editing ? "Edit product" : "Add new product"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Name" required value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} data-testid="np-name" />
                <Select value={newProduct.category} onValueChange={(v) => setNewProduct({ ...newProduct, category: v })}>
                  <SelectTrigger data-testid="np-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resin-art">Resin Art</SelectItem>
                    <SelectItem value="handmade-bags">Handmade Bags</SelectItem>
                    <SelectItem value="pearl-jewellery">Pearl Jewellery</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Short description" value={newProduct.short_description} onChange={(e) => setNewProduct({ ...newProduct, short_description: e.target.value })} className="md:col-span-2" />
                <Textarea placeholder="Long description" required value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className="md:col-span-2 min-h-[100px]" data-testid="np-desc" />
                <Input type="number" placeholder="Price" required value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} data-testid="np-price" />
                <Input type="number" placeholder="Compare at price (optional)" value={newProduct.compare_at_price || ""} onChange={(e) => setNewProduct({ ...newProduct, compare_at_price: e.target.value })} />
                <Input type="number" placeholder="Stock" required value={newProduct.stock} onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })} data-testid="np-stock" />
                <Input placeholder="Dimensions" value={newProduct.dimensions} onChange={(e) => setNewProduct({ ...newProduct, dimensions: e.target.value })} />
                <Input placeholder="Materials (comma-separated)" value={(newProduct.materials || []).join(", ")} onChange={(e) => setNewProduct({ ...newProduct, materials: e.target.value.split(",").map((s) => s.trim()) })} className="md:col-span-2" />
                <div className="md:col-span-2">
                  <div className="overline text-charcoal/60 mb-2">Images</div>
                  <div className="border border-dashed divider-gold p-6 text-center">
                    <input
                      id="np-files"
                      data-testid="np-files"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { uploadProductImages(e.target.files); e.target.value = ""; }}
                    />
                    <label htmlFor="np-files" className="cursor-pointer inline-flex items-center gap-2 overline text-emerald">
                      <Upload className="w-4 h-4" /> {uploadingImages ? "Uploading…" : "Choose photos"}
                    </label>
                    <div className="text-[11px] text-charcoal/50 mt-2">Up to 8MB each · JPG / PNG / WebP</div>
                  </div>
                  {newProduct.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-3">
                      {newProduct.images.map((u, i) => (
                        <div key={u} className="relative" data-testid={`np-img-${i}`}>
                          <img src={u} alt="" className="w-full aspect-square object-cover bg-ivory" />
                          <button type="button" onClick={() => removeProductImage(u)} className="absolute top-1 right-1 p-1 bg-cream/95">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-4 md:col-span-2 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newProduct.is_new_arrival} onChange={(e) => setNewProduct({ ...newProduct, is_new_arrival: e.target.checked })} /> New arrival</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newProduct.is_best_seller} onChange={(e) => setNewProduct({ ...newProduct, is_best_seller: e.target.checked })} /> Best seller</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newProduct.is_featured} onChange={(e) => setNewProduct({ ...newProduct, is_featured: e.target.checked })} /> Featured</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newProduct.is_active} onChange={(e) => setNewProduct({ ...newProduct, is_active: e.target.checked })} /> Active</label>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button type="submit" data-testid="np-save" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">{editing ? "Update" : "Create product"}</Button>
                {editing && <Button type="button" variant="outline" onClick={() => { setEditing(null); setNewProduct(emptyProduct); }} className="rounded-none">Cancel</Button>}
              </div>
            </form>

            <div className="border divider-gold">
              <div className="grid grid-cols-[80px_1fr_120px_100px_100px_120px] gap-4 p-4 overline text-charcoal/60 border-b divider-gold text-xs">
                <div>Image</div><div>Name</div><div>Category</div><div>Price</div><div>Stock</div><div>Actions</div>
              </div>
              {products.map((p) => (
                <div key={p.id} className="grid grid-cols-[80px_1fr_120px_100px_100px_120px] gap-4 p-4 items-center border-b divider-gold text-sm">
                  <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                  <div className="font-serif">{p.name}</div>
                  <div className="text-xs">{p.category}</div>
                  <div>{currency(p.price)}</div>
                  <div>{p.stock}</div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(p)} className="overline text-emerald" data-testid={`edit-${p.slug}`}>Edit</button>
                    <button onClick={() => deleteProduct(p.id)} data-testid={`del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="border divider-gold">
              <div className="grid grid-cols-[1fr_1fr_120px_130px_1fr_160px] gap-4 p-4 overline text-charcoal/60 border-b divider-gold text-xs">
                <div>Order</div><div>Customer</div><div>Total</div><div>Payment</div><div>Status</div><div>Update</div>
              </div>
              {orders.map((o) => (
                <div key={o.id} className="grid grid-cols-[1fr_1fr_120px_130px_1fr_160px] gap-4 p-4 items-center border-b divider-gold text-sm">
                  <div>#{o.order_number}<div className="text-xs text-charcoal/50">{new Date(o.created_at).toLocaleDateString()}</div></div>
                  <div>{o.address?.full_name}<div className="text-xs text-charcoal/50">{o.address?.city}</div></div>
                  <div>{currency(o.total)}</div>
                  <div className="text-xs">{o.payment_method}</div>
                  <div className="text-xs uppercase tracking-widest">{o.status}</div>
                  <Select onValueChange={(v) => updateOrderStatus(o.id, v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Change…" /></SelectTrigger>
                    <SelectContent>
                      {["paid", "shipped", "delivered", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="custom">
            <h2 className="font-serif text-2xl mb-6">Custom orders</h2>
            {customOrders.length === 0 ? (
              <div className="py-16 text-charcoal/50 italic">No custom requests yet.</div>
            ) : (
              <div className="space-y-3">
                {customOrders.map((co) => (
                  <button key={co.id} onClick={() => setOpenCustom(co.id)} className="w-full text-left p-4 bg-ivory/40 border divider-gold hover:border-emerald" data-testid={`adm-co-${co.request_number}`}>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_180px_auto] gap-3 items-center">
                      <div>
                        <div className="font-serif">#{co.request_number}</div>
                        <div className="text-xs text-charcoal/60">{new Date(co.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="text-sm">{co.name}<div className="text-xs text-charcoal/60">{co.email}</div></div>
                      <div className="text-xs">{co.product_type}</div>
                      <div><StatusStrip status={co.status} /></div>
                      <div className="text-right font-serif">{co.quote_price ? currency(co.quote_price) : "—"}</div>
                    </div>
                    <p className="text-sm text-charcoal/70 mt-2 line-clamp-1">{co.description}</p>
                  </button>
                ))}
              </div>
            )}
            {openCustom && <CustomOrderDetail orderId={openCustom} isAdmin onClose={() => setOpenCustom(null)} onChange={load} />}
          </TabsContent>


          <TabsContent value="customers">
            <div className="border divider-gold">
              <div className="grid grid-cols-[1fr_1fr_1fr_120px] gap-4 p-4 overline text-charcoal/60 border-b divider-gold text-xs">
                <div>Name</div><div>Email</div><div>Joined</div><div>Wishlist</div>
              </div>
              {customers.map((c) => (
                <div key={c.id} className="grid grid-cols-[1fr_1fr_1fr_120px] gap-4 p-4 border-b divider-gold text-sm">
                  <div>{c.name}</div><div className="text-charcoal/70">{c.email}</div>
                  <div className="text-xs">{new Date(c.created_at).toLocaleDateString()}</div>
                  <div>{(c.wishlist || []).length}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="coupons">
            <form onSubmit={createCoupon} className="p-6 bg-ivory/60 border divider-gold mb-8 grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input placeholder="Code" required value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} data-testid="nc-code" />
              <Select value={newCoupon.discount_type} onValueChange={(v) => setNewCoupon({ ...newCoupon, discount_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed ₹</SelectItem></SelectContent>
              </Select>
              <Input type="number" placeholder="Amount" required value={newCoupon.amount} onChange={(e) => setNewCoupon({ ...newCoupon, amount: Number(e.target.value) })} />
              <Input type="number" placeholder="Min order" value={newCoupon.min_order} onChange={(e) => setNewCoupon({ ...newCoupon, min_order: Number(e.target.value) })} />
              <Button type="submit" className="rounded-none bg-emerald text-ivory">Save</Button>
            </form>
            <div className="border divider-gold">
              {coupons.map((c) => (
                <div key={c.code} className="flex items-center justify-between p-4 border-b divider-gold">
                  <div><span className="font-mono">{c.code}</span> — {c.discount_type === "percent" ? `${c.amount}%` : currency(c.amount)} off (min ₹{c.min_order})</div>
                  <button onClick={async () => { await api.delete(`/admin/coupons/${c.code}`); await load(); }} className="text-charcoal/60 hover:text-emerald"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="p-4 bg-ivory/40 border divider-gold">
                  <div className="flex items-baseline justify-between">
                    <div className="font-serif text-lg">{r.title || "(no title)"} · ★ {r.rating}</div>
                    <div className="flex gap-2">
                      <button onClick={() => moderateReview(r.id, !r.approved)} className="overline text-emerald">{r.approved ? "Hide" : "Approve"}</button>
                    </div>
                  </div>
                  <div className="text-xs text-charcoal/60 mt-1">{r.user_name} · {r.verified_purchase ? "Verified" : "Guest"}</div>
                  <p className="text-sm mt-2">{r.comment}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="content">
            <div className="space-y-12">
              <div>
                <h3 className="font-serif text-2xl mb-4">Homepage banners</h3>
                <form onSubmit={createBanner} className="p-6 bg-ivory/60 border divider-gold grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <Input placeholder="Title" required value={newBanner.title} onChange={(e) => setNewBanner({ ...newBanner, title: e.target.value })} />
                  <Input placeholder="Subtitle" value={newBanner.subtitle} onChange={(e) => setNewBanner({ ...newBanner, subtitle: e.target.value })} />
                  <Input placeholder="Image URL" required value={newBanner.image} onChange={(e) => setNewBanner({ ...newBanner, image: e.target.value })} className="md:col-span-2" />
                  <Input placeholder="CTA text" value={newBanner.cta_text} onChange={(e) => setNewBanner({ ...newBanner, cta_text: e.target.value })} />
                  <Input placeholder="CTA link" value={newBanner.cta_link} onChange={(e) => setNewBanner({ ...newBanner, cta_link: e.target.value })} />
                  <Button type="submit" className="md:col-span-2 rounded-none bg-emerald text-ivory">Add banner</Button>
                </form>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {banners.map((b) => (
                    <div key={b.id} className="border divider-gold">
                      <img src={b.image} className="w-full h-32 object-cover" alt="" />
                      <div className="p-3 flex items-center justify-between text-sm">
                        <div className="font-serif">{b.title}</div>
                        <button onClick={async () => { await api.delete(`/admin/banners/${b.id}`); await load(); }}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-serif text-2xl mb-4">Testimonials</h3>
                <form onSubmit={createTestimonial} className="p-6 bg-ivory/60 border divider-gold grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <Input placeholder="Name" required value={newTest.name} onChange={(e) => setNewTest({ ...newTest, name: e.target.value })} />
                  <Input placeholder="Role / city" value={newTest.role} onChange={(e) => setNewTest({ ...newTest, role: e.target.value })} />
                  <Textarea placeholder="Quote" required value={newTest.quote} onChange={(e) => setNewTest({ ...newTest, quote: e.target.value })} className="md:col-span-2 min-h-[80px]" />
                  <Button type="submit" className="md:col-span-2 rounded-none bg-emerald text-ivory">Add testimonial</Button>
                </form>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {testimonials.map((t) => (
                    <div key={t.id} className="p-4 bg-ivory/60 border divider-gold">
                      <div className="text-sm italic">"{t.quote}"</div>
                      <div className="text-xs text-charcoal/60 mt-2">— {t.name}, {t.role}</div>
                      <button onClick={async () => { await api.delete(`/admin/testimonials/${t.id}`); await load(); }} className="mt-2 text-charcoal/60 hover:text-emerald"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-serif text-2xl mb-4">Gallery</h3>
                <form onSubmit={createGallery} className="p-6 bg-ivory/60 border divider-gold grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <Input placeholder="Image or video URL" required value={newGallery.url} onChange={(e) => setNewGallery({ ...newGallery, url: e.target.value })} />
                  <Select value={newGallery.category} onValueChange={(v) => setNewGallery({ ...newGallery, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resin-art">Resin</SelectItem>
                      <SelectItem value="handmade-bags">Bags</SelectItem>
                      <SelectItem value="pearl-jewellery">Pearl</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Caption" value={newGallery.caption} onChange={(e) => setNewGallery({ ...newGallery, caption: e.target.value })} className="md:col-span-2" />
                  <Button type="submit" className="md:col-span-2 rounded-none bg-emerald text-ivory">Add to gallery</Button>
                </form>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {galleryItems.map((g) => (
                    <div key={g.id} className="relative">
                      <img src={g.url} alt="" className="w-full aspect-square object-cover" />
                      <button onClick={async () => { await api.delete(`/admin/gallery/${g.id}`); await load(); }} className="absolute top-1 right-1 p-1 bg-cream/90"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-serif text-2xl mb-4">Contact requests</h3>
                {contacts.map((c) => (
                  <div key={c.id} className="p-4 bg-ivory/40 border divider-gold mb-3">
                    <div className="text-sm font-serif">{c.subject} — {c.name}</div>
                    <div className="text-xs text-charcoal/60">{c.email} · {c.phone}</div>
                    <p className="text-sm mt-2">{c.message}</p>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="font-serif text-2xl mb-4">Newsletter subscribers</h3>
                <div className="border divider-gold">
                  {subs.map((s) => (
                    <div key={s.email} className="p-3 border-b divider-gold text-sm flex justify-between">
                      <span>{s.email}</span><span className="text-xs text-charcoal/50">{s.subscribed_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
